import React, {useRef, useCallback} from 'react';
import {useTooltipTriggerState} from '@react-stately/tooltip';
import {useTooltipTrigger} from '@react-aria/tooltip';
import {Overlay, useOverlayPosition} from '@react-aria/overlays';

import type {TooltipPosition, ExtendedTooltipPosition} from '..';
import {mapTooltipPosition} from '../components/elements/Tooltip/Tooltip.utils';

/**
 * Main axis size of the arrow, i.e. how far it sticks out of the tooltip body.
 * Keep in sync with `$arrow-height` in `Tooltip.module.scss`.
 */
const ARROW_HEIGHT = 9;

/**
 * Cross axis size of the arrow.
 * Keep in sync with `$arrow-width` in `Tooltip.module.scss`.
 */
const ARROW_WIDTH = 20;

/**
 * Minimum distance between the centre of the arrow and the tooltip corner, so
 * the arrow never ends up on the rounded corners.
 */
const ARROW_BOUNDARY_OFFSET = 12;

/**
 * Visible gap left between the trigger and the tooltip, measured from the arrow
 * tip for the tooltips that render one.
 */
export const TOOLTIP_GAP = 4;

/**
 * Minimum distance the tooltip keeps from the edges of the viewport. Together
 * with `shouldFlip` this is what keeps the tooltip on screen when the page is
 * zoomed in heavily.
 */
const VIEWPORT_PADDING = 8;

export const useAriaTooltip = ({
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
    isOpen,
    closeDelay,
    defaultOpen,
    delay,
    id,
    onOpenChange,
    overlayRef: providedOverlayRef,
    crossOffset,
    offset = ARROW_HEIGHT + TOOLTIP_GAP,
    position,
    role = 'tooltip',
    targetRef: providedTargetRef,
    zIndex,
}: {
    ariaDescribedBy?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    isOpen?: boolean;
    closeDelay?: number;
    crossOffset?: number;
    defaultOpen?: boolean;
    delay?: number;
    id?: string;
    onOpenChange?: (isOpen: boolean) => void;
    overlayRef?: React.RefObject<HTMLDivElement | null>;
    offset?: number;
    position: TooltipPosition | ExtendedTooltipPosition;
    role?: React.AriaRole;
    targetRef?: React.RefObject<HTMLDivElement | null>;
    zIndex?: number | string;
}) => {
    const state = useTooltipTriggerState({
        closeDelay,
        defaultOpen,
        delay,
        isOpen,
        onOpenChange,
    });
    const internalTargetRef = useRef<HTMLDivElement>(null);
    const internalOverlayRef = useRef<HTMLDivElement>(null);
    const targetRef = providedTargetRef ?? internalTargetRef;
    const overlayRef = providedOverlayRef ?? internalOverlayRef;
    const {triggerProps, tooltipProps} = useTooltipTrigger(
        {},
        state,
        targetRef,
    );

    const isRtl = document?.documentElement?.getAttribute('dir') === 'rtl';

    const {overlayProps, arrowProps, placement} = useOverlayPosition({
        arrowBoundaryOffset: ARROW_BOUNDARY_OFFSET,
        arrowSize: ARROW_WIDTH,
        containerPadding: VIEWPORT_PADDING,
        crossOffset,
        isOpen: state.isOpen,
        offset,
        overlayRef,
        placement: mapTooltipPosition(position, isRtl),
        shouldFlip: true,
        targetRef,
    });

    /**
     * Renders the tooltip content into an overlay portal. The overlay element
     * has to directly wrap the visible tooltip body, otherwise
     * `useOverlayPosition` measures an empty box and can neither flip the
     * tooltip nor keep it within the viewport.
     *
     * `useOverlayPosition` can move the tooltip but not shrink it, so the
     * overlay is additionally capped to the viewport width. The percentage
     * resolves against the initial containing block, i.e. the viewport without
     * its scrollbars.
     */
    const renderTooltip = useCallback(
        (content: React.ReactNode) =>
            state.isOpen ? (
                <Overlay>
                    {/*
                     * `data-react-aria-top-layer` marks this portalled tooltip as
                     * top-layer content so react-aria's `ariaHideOutside` (used by
                     * modal popovers/dialogs) keeps it interactive instead of
                     * applying `inert` to it, which would block pointer/hover/focus
                     * (e.g. the tooltip's close button) while the modal is open.
                     */}
                    <div
                        {...tooltipProps}
                        {...overlayProps}
                        style={{
                            ...overlayProps.style,
                            ...(zIndex === undefined
                                ? {}
                                : {
                                      zIndex: zIndex as React.CSSProperties['zIndex'],
                                  }),
                            maxWidth: `calc(100% - ${VIEWPORT_PADDING * 2}px)`,
                        }}
                        ref={overlayRef}
                        id={id}
                        role={role}
                        {...(role === 'dialog'
                            ? {
                                  'aria-describedby': ariaDescribedBy,
                                  'aria-label': ariaLabel,
                                  'aria-labelledby': ariaLabelledBy,
                              }
                            : {})}
                        data-react-aria-top-layer="true"
                    >
                        {content}
                    </div>
                </Overlay>
            ) : null,
        [
            ariaDescribedBy,
            ariaLabel,
            ariaLabelledBy,
            id,
            overlayRef,
            overlayProps,
            role,
            state.isOpen,
            tooltipProps,
            zIndex,
        ],
    );

    return {
        targetRef,
        overlayRef,
        triggerProps,
        arrowProps,
        placement,
        renderTooltip,
    };
};
